import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { useAppSelector } from 'hooks/reduxHooks'
import useLuxPlusRenewalBonus, { getCurrentRenewalRewardStep } from 'luxPlus/hooks/useLuxPlusRenewalBonus'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import getLoyaltyNextTier from 'loyalty/selectors/getLoyaltyNextTier'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { getLuxPlusSubscriptionRenewalDate } from 'selectors/luxPlusSelectors'
import { daysUntilDate } from 'lib/datetime/dateUtils'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import useJoinSociete from 'luxPlus/hooks/useJoinSociete'
import nonSocieteRenewalBonusMessages from './nonSocieteRenewalBonusMessages'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import LuxPlusRenewalBonusTimeline, {
  renewalBonusMessages,
  useRenewalRewardAmountText,
  RenewalBonusInnerCard,
  RenewalBonusLadderBox,
  RenewalBonusGiftIcon,
  RenewalTextSkeleton,
} from '../LandingPage/LuxPlusRenewalBonusTimeline'
import LuxPlusRetentionModalFooter from './LuxPlusRetentionModalFooter'

interface Props {
  onContinueToCancel: () => void
  onBack: () => void
}

const messages = defineMessages({
  titleImminent: {
    id: '7_8vWZKq4NCc5fd-2kJd',
    defaultMessage: 'Your renewal bonus is almost here',
    description: 'Title when the renewal (and its bonus) is imminent',
  },
  titleDaysAway: {
    id: '3eZKmf77zsQz2B4Xpbgx',
    defaultMessage: "You're just {days} days away from your renewal bonus",
    description: 'Retention modal title shown when the renewal bonus is within 30 days away',
  },
  titleGettingCloser: {
    id: 'B-5i2iTckIV2Be5GCAxk',
    defaultMessage: 'Your next renewal bonus is getting closer',
    description: 'Retention modal title shown when the renewal bonus is 31 to 90 days away',
  },
  titleNext: {
    id: 'CfWJqP_odasbLeNZ9lo1',
    defaultMessage: 'Your next renewal bonus',
    description: 'Title when the renewal bonus is more than 90 days away',
  },
  subtitle: {
    id: '70jw2AdwCjrtPKbcnRdz',
    defaultMessage: 'Remain a {membershipName} member and receive your renewal bonus on <b>{date}</b>.',
    description: 'Subtitle telling the member when they receive their renewal bonus',
  },
  titleJoin: {
    id: 'jjlfXtGvpxxnCWFJeyS1',
    defaultMessage: 'Join Société to receive your renewal bonus',
    description: 'Cancel-renewal retention title for a LuxPlus+ member who has not joined Société',
  },
  eyebrow: {
    id: 'Yy6aLJDN5OC0A-owwaAa',
    defaultMessage: 'Your year {year} renewal bonus',
    description: 'Eyebrow label above the renewal bonus amount in the retention modal reward card',
  },
})

function LuxPlusManageAccountModalRenewalBonusRetention({ onContinueToCancel, onBack }: Props) {
  const intl = useIntl()

  const { steps, isLoading } = useLuxPlusRenewalBonus()
  const isSocieteMember = useAppSelector(getHasLoyaltyAccount)
  const joinSociete = useJoinSociete()
  const hideStatusCredits = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)
  const isInfinite = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  // Top tiers have no next tier to progress to: platinum yields no next tier, and infinite sits
  // outside the public tier ladder entirely. Non-Société members have no tier yet.
  const nextTier = useAppSelector(getLoyaltyNextTier)
  const showNextTierBlurb = isSocieteMember && !!nextTier && !isInfinite
  const renewalDate = useAppSelector(getLuxPlusSubscriptionRenewalDate)
  const formatRewardAmount = useRenewalRewardAmountText()

  // Highlight the 1st renewal as current when there's no active progression yet.
  const displaySteps = steps.some((step) => step.state === 'current')
    ? steps
    : steps.map((step, index) => (index === 0 ? { ...step, state: 'current' as const } : step))
  const nextReward = getCurrentRenewalRewardStep(displaySteps)
  const renewalYear = nextReward?.year ?? 1
  const rewardAmountText = formatRewardAmount(nextReward, { hideStatusCredits })
  const points = nextReward?.points
  const statusCredits = nextReward?.statusCredits
  const showJoinStatusCredits = points !== undefined && !hideStatusCredits && statusCredits !== undefined

  const daysUntilRenewal = renewalDate ? daysUntilDate(new Date(renewalDate)) : undefined
  const title = !isSocieteMember
    ? intl.formatMessage(messages.titleJoin)
    : daysUntilRenewal === undefined
      ? intl.formatMessage(messages.titleNext)
      : daysUntilRenewal <= 1
        ? intl.formatMessage(messages.titleImminent)
        : daysUntilRenewal <= 30
          ? intl.formatMessage(messages.titleDaysAway, { days: daysUntilRenewal })
          : daysUntilRenewal <= 90
            ? intl.formatMessage(messages.titleGettingCloser)
            : intl.formatMessage(messages.titleNext)

  return <>
    <ModalHeader title={title} onBackButtonClick={onBack} />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={20}>
          {isSocieteMember && renewalDate && <BodyText variant="medium" colour="light-foreground-alt">
            <FormattedMessage
              {...messages.subtitle}
              values={{
                membershipName: LUXURY_PLUS.PROGRAM_NAME,
                date: <FormatDateTime value={renewalDate} format="dmy-casual" />,
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </BodyText>}
          {!isSocieteMember && isLoading && <RenewalTextSkeleton lineWidths={[320, 300, 160]} height={18} />}
          {!isSocieteMember && !isLoading && points !== undefined && renewalDate && <BodyText variant="medium" colour="light-foreground-alt">
            <FormattedMessage
              {...(showJoinStatusCredits ? nonSocieteRenewalBonusMessages.joinBonusFull : nonSocieteRenewalBonusMessages.joinBonusPoints)}
              values={{
                date: <FormatDateTime value={renewalDate} dateStyle="medium" />,
                b: (chunks) => <b>{chunks}</b>,
                link: (chunks) => <TextLink weight="regular" onClick={joinSociete}>{chunks}</TextLink>,
                loyaltyPoints: <FormatLoyaltyPoints points={points} />,
                statusCredits: statusCredits !== undefined ? <FormatLoyaltyStatusCredits statusCredits={statusCredits} /> : undefined,
              }}
            />
          </BodyText>}
          <Group direction="vertical" gap={12}>
            <RenewalBonusInnerCard>
              <Group direction="vertical" gap={12}>
                <Group direction="vertical" gap={4}>
                  <Group direction="horizontal" gap={8} verticalAlign="center">
                    <RenewalBonusGiftIcon size="XS" />
                    <BodyTextBlock variant="medium" weight="bold" colour="lux-plus">
                      {intl.formatMessage(messages.eyebrow, { year: renewalYear })}
                    </BodyTextBlock>
                  </Group>
                  <HeadingTextBlock variant="heading3">{rewardAmountText}</HeadingTextBlock>
                  {showNextTierBlurb && <BodyText variant="medium" colour="light-foreground-alt">
                    {intl.formatMessage(renewalBonusMessages.nextTierBlurb, { nextTier: getLoyaltyTierDisplayName(nextTier) })}
                  </BodyText>}
                </Group>
                {/* Infinite members earn points only — no Status Credits or tier ladder to show. */}
                {!isInfinite && <RenewalBonusLadderBox>
                  <LuxPlusRenewalBonusTimeline
                    steps={displaySteps}
                    isLoading={isLoading}
                    currentStepCaption={intl.formatMessage(renewalBonusMessages.currentStepAlmostHere)}
                    desktopAmountsMaxWidthRem={128}
                  />
                </RenewalBonusLadderBox>}
              </Group>
            </RenewalBonusInnerCard>
            <CaptionTextBlock variant="medium" colour="light-foreground-alt">
              {intl.formatMessage(renewalBonusMessages.footnote, { membershipName: LUXURY_PLUS.PROGRAM_NAME })}
            </CaptionTextBlock>
          </Group>
        </Group>
      </ModalContent>
    </ModalBody>
    <LuxPlusRetentionModalFooter onContinueToCancel={onContinueToCancel} />
  </>
}

export default LuxPlusManageAccountModalRenewalBonusRetention
